/**
 * `.onScrollGeometryChange` (docs/ops-protocol.md, "Scroll geometry, pinned
 * views and animation completion"): a `scrollview` with `reportsGeometry`
 * tells the app where its visible area is in its content and how big both
 * are, as SwiftUI's `ScrollGeometry` does. The renderer reads the offsets
 * from the element (the engine's frames put every scroll view at offset 0)
 * and the sizes from the layout result, after every layout pass and once
 * per animation frame while anything scrolls; the reporter sends an event
 * only when a field changed since the last one, so a pass that moved
 * nothing says nothing.
 */

import type { Rect, Size } from './layout';
import type { ElementId, ScrollGeometryEvent } from './protocol';

export interface ScrollGeometry {
  offset: { x: number; y: number };
  contentSize: Size;
  containerSize: Size;
  contentInsets: { top: number; leading: number; bottom: number; trailing: number };
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

/** The event for a geometry, every number rounded to 2 decimals. */
export function scrollGeometryEvent(id: ElementId, geometry: ScrollGeometry): ScrollGeometryEvent {
  const { offset, contentSize, containerSize, contentInsets } = geometry;
  return {
    type: 'scrollgeometry',
    id,
    offset: { x: round2(offset.x), y: round2(offset.y) },
    contentSize: { width: round2(contentSize.width), height: round2(contentSize.height) },
    containerSize: { width: round2(containerSize.width), height: round2(containerSize.height) },
    contentInsets: {
      top: round2(contentInsets.top),
      leading: round2(contentInsets.leading),
      bottom: round2(contentInsets.bottom),
      trailing: round2(contentInsets.trailing),
    },
  };
}

/** Sends each scroll view's geometry once per change. */
export class ScrollGeometryReporter {
  private readonly last = new Map<ElementId, string>();

  /** The event to send for this geometry, or null when it is the one last sent for the element. */
  report(id: ElementId, geometry: ScrollGeometry): ScrollGeometryEvent | null {
    const event = scrollGeometryEvent(id, geometry);
    const key = JSON.stringify([event.offset, event.contentSize, event.containerSize, event.contentInsets]);
    if (this.last.get(id) === key) return null;
    this.last.set(id, key);
    return event;
  }

  /** The element was removed: one re-created with the same id reports afresh. */
  forget(id: ElementId): void {
    this.last.delete(id);
  }

  clear(): void {
    this.last.clear();
  }
}

/**
 * A scroll view's geometry now: its offsets from the element, its sizes
 * from the layout pass (`frame`, `contentSize`; the element's own when the
 * pass has none), and the space below its content a bar covers
 * (`bottomInset`), which it scrolls into.
 */
export function readScrollGeometry(el: HTMLElement, frame: Rect | undefined, contentSize: Size | undefined, bottomInset: number): ScrollGeometry {
  return {
    offset: { x: el.scrollLeft, y: el.scrollTop },
    contentSize: contentSize ?? { width: el.scrollWidth, height: el.scrollHeight - bottomInset },
    containerSize: frame ? { width: frame.width, height: frame.height } : { width: el.clientWidth, height: el.clientHeight },
    contentInsets: { top: 0, leading: 0, bottom: bottomInset, trailing: 0 },
  };
}
